import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { MapContainer, TileLayer, Marker, Tooltip, Polyline, useMap, CircleMarker } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import type { UserLocation } from '@/hooks/useGeolocation'

interface StopInfo {
  StopCode: string
  StopDescr: string
  StopDescrEng: string
  StopLat: string
  StopLng: string
  RouteStopOrder: string
}

interface RouteDirection {
  from: string
  to: string
}

interface RouteMapProps {
  stops: StopInfo[]
  selectedStop: StopInfo | null
  onStopSelect: (stop: StopInfo) => void
  lineId: string
  lang: 'el' | 'en'
  searchQuery: string
  onSearchChange: (query: string) => void
  routeDirection?: RouteDirection
  userLocation?: UserLocation | null
}

const stopIcon = new L.DivIcon({
  className: 'stop-marker',
  html: '<div class="stop-marker-inner"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
  popupAnchor: [0, -8]
})

const selectedStopIcon = new L.DivIcon({
  className: 'stop-marker selected',
  html: '<div class="stop-marker-inner selected"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12]
})

const highlightedStopIcon = new L.DivIcon({
  className: 'stop-marker highlighted',
  html: '<div class="stop-marker-inner highlighted"></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -10]
})

const startStopIcon = new L.DivIcon({
  className: 'stop-marker start-stop',
  html: '<div class="stop-marker-inner start-stop"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12]
})

const endStopIcon = new L.DivIcon({
  className: 'stop-marker end-stop',
  html: '<div class="stop-marker-inner end-stop"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12]
})

function FitBounds({ stops }: { stops: StopInfo[] }) {
  const map = useMap()

  useEffect(() => {
    if (stops.length === 0) return

    const bounds = L.latLngBounds(
      stops.map(s => [parseFloat(s.StopLat), parseFloat(s.StopLng)] as [number, number])
    )
    map.fitBounds(bounds, { padding: [40, 40] })
  }, [stops, map])

  return null
}

function FlyToStop({ stop }: { stop: StopInfo | null }) {
  const map = useMap()

  useEffect(() => {
    if (!stop) return
    map.flyTo([parseFloat(stop.StopLat), parseFloat(stop.StopLng)], 16, { duration: 0.5 })
  }, [stop, map])

  return null
}

function FlyToLocation({ location, trigger }: { location: UserLocation | null; trigger: number }) {
  const map = useMap()

  useEffect(() => {
    if (!location || trigger === 0) return
    map.flyTo([location.lat, location.lng], 16, { duration: 0.5 })
  }, [location, trigger, map])

  return null
}

export function RouteMap({
  stops,
  selectedStop,
  onStopSelect,
  lineId,
  lang,
  searchQuery,
  onSearchChange,
  routeDirection,
  userLocation
}: RouteMapProps) {
  const [filteredStops, setFilteredStops] = useState<StopInfo[]>([])
  const [flyToUserTrigger, setFlyToUserTrigger] = useState(0)
  const mapRef = useRef<L.Map | null>(null)

  const getStopName = (stop: StopInfo) =>
    lang === 'en' ? (stop.StopDescrEng || stop.StopDescr) : stop.StopDescr

  useEffect(() => {
    if (!searchQuery) {
      setFilteredStops([])
      return
    }
    const query = searchQuery.toLowerCase()
    const matches = stops.filter(s => {
      const name = getStopName(s).toLowerCase()
      return name.includes(query)
    })
    setFilteredStops(matches)
  }, [searchQuery, stops, lang])

  const routeCoordinates: [number, number][] = stops.map(s => [
    parseFloat(s.StopLat),
    parseFloat(s.StopLng)
  ])

  const defaultCenter: [number, number] = stops.length > 0
    ? [parseFloat(stops[0].StopLat), parseFloat(stops[0].StopLng)]
    : [37.9838, 23.7275]

  const isHighlighted = (stop: StopInfo) =>
    filteredStops.length > 0 && filteredStops.some(s => s.StopCode === stop.StopCode)

  const isFirstStop = (stop: StopInfo) => stops.length > 0 && stops[0].StopCode === stop.StopCode
  const isLastStop = (stop: StopInfo) => stops.length > 0 && stops[stops.length - 1].StopCode === stop.StopCode

  const getIcon = (stop: StopInfo) => {
    if (selectedStop?.StopCode === stop.StopCode) return selectedStopIcon
    if (isFirstStop(stop)) return startStopIcon
    if (isLastStop(stop)) return endStopIcon
    if (isHighlighted(stop)) return highlightedStopIcon
    return stopIcon
  }

  return (
    <div className="route-map-container">
      <div className="map-search-bar">
        <input
          type="text"
          placeholder={lang === 'el' ? 'Αναζήτηση στάσης...' : 'Search stop...'}
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
          className="map-search-input"
        />
        {searchQuery && (
          <button className="map-search-clear" onClick={() => onSearchChange('')}>
            ×
          </button>
        )}
      </div>

      {searchQuery && filteredStops.length > 0 && (
        <div className="map-search-results">
          {filteredStops.slice(0, 5).map(stop => (
            <div
              key={stop.StopCode}
              className="map-search-result-item"
              onClick={() => {
                onStopSelect(stop)
                onSearchChange('')
              }}
            >
              <span className="result-order">{stop.RouteStopOrder}</span>
              <span className="result-name">{getStopName(stop)}</span>
            </div>
          ))}
        </div>
      )}

      {userLocation && (
        <button
          className="map-location-btn"
          onClick={() => setFlyToUserTrigger(t => t + 1)}
          title={lang === 'el' ? 'Η τοποθεσία μου' : 'My location'}
        >
          📍
        </button>
      )}

      {routeDirection && (
        <div className="map-route-direction">
          <span className="route-start-label">{routeDirection.from}</span>
          <span className="route-direction-arrow">➜</span>
          <span className="route-end-label">{routeDirection.to}</span>
        </div>
      )}

      <MapContainer
        center={defaultCenter}
        zoom={13}
        className="route-map"
        ref={mapRef}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <FitBounds stops={stops} />
        <FlyToStop stop={selectedStop} />
        <FlyToLocation location={userLocation || null} trigger={flyToUserTrigger} />

        <Polyline
          positions={routeCoordinates}
          pathOptions={{
            color: '#3b82f6',
            weight: 4,
            opacity: 0.8,
            lineCap: 'round',
            lineJoin: 'round'
          }}
        />

        {stops.map(stop => {
          const stopUrl = `/${lineId.toLowerCase()}/${encodeURIComponent(getStopName(stop).toLowerCase().replace(/\s+/g, '-'))}`
          return (
            <Marker
              key={stop.StopCode}
              position={[parseFloat(stop.StopLat), parseFloat(stop.StopLng)]}
              icon={getIcon(stop)}
              eventHandlers={{
                click: () => onStopSelect(stop)
              }}
            >
              <Tooltip
                direction="top"
                offset={[0, -12]}
                className="stop-tooltip"
              >
                <Link to={stopUrl} className="stop-tooltip-link" onClick={(e) => {
                  e.preventDefault()
                  onStopSelect(stop)
                }}>
                  {getStopName(stop)}
                </Link>
              </Tooltip>
            </Marker>
          )
        })}

        {userLocation && (
          <CircleMarker
            center={[userLocation.lat, userLocation.lng]}
            radius={10}
            pathOptions={{
              color: '#fff',
              weight: 3,
              fillColor: '#4285f4',
              fillOpacity: 1,
            }}
          >
            <Tooltip direction="top" offset={[0, -10]} className="stop-tooltip">
              {lang === 'el' ? 'Η τοποθεσία σας' : 'Your location'}
            </Tooltip>
          </CircleMarker>
        )}
      </MapContainer>

      <div className="map-stop-list">
        <div className="map-stop-list-header">
          {lang === 'el' ? 'Στάσεις' : 'Stops'} ({stops.length})
        </div>
        <div className="map-stop-list-items">
          {stops.map(stop => {
            const isStart = isFirstStop(stop)
            const isEnd = isLastStop(stop)
            return (
              <div
                key={stop.StopCode}
                className={`map-stop-item ${selectedStop?.StopCode === stop.StopCode ? 'selected' : ''} ${isHighlighted(stop) ? 'highlighted' : ''} ${isStart ? 'start-stop' : ''} ${isEnd ? 'end-stop' : ''}`}
                onClick={() => onStopSelect(stop)}
              >
                <span className={`map-stop-order ${isStart ? 'start' : ''} ${isEnd ? 'end' : ''}`}>{stop.RouteStopOrder}</span>
                <span className={`map-stop-name ${isStart ? 'start' : ''} ${isEnd ? 'end' : ''}`}>{getStopName(stop)}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
